import { useEffect, useRef, useState } from "react";
import { useAuthContext } from "@/providers/AuthProvider";
import { useI18n } from "@/i18n";

interface GoogleSignInButtonProps {
  className?: string;
}

// Detect if running in an in-app browser (WebView)
function isInAppBrowser(): boolean {
  if (typeof window === "undefined") return false;

  const ua = navigator.userAgent || navigator.vendor || "";

  // Common in-app browser patterns
  const inAppPatterns = [
    /FBAN|FBAV/i,            // Facebook
    /FB_IAB/i,               // Facebook In-App Browser
    /Instagram/i,            // Instagram
    /Twitter/i,              // Twitter/X
    /Line\//i,               // LINE
    /KAKAOTALK/i,            // KakaoTalk
    /Barcelona/i,            // Threads (uses Barcelona as code name)
    /Threads/i,              // Threads (fallback)
    /Messenger/i,            // Facebook Messenger
    /WeChat|MicroMessenger/i, // WeChat
    /Snapchat/i,             // Snapchat
    /TikTok|BytedanceWebview|musical_ly/i, // TikTok
    /LinkedInApp/i,          // LinkedIn
    /Pinterest/i,            // Pinterest
    /Telegram/i,             // Telegram
    /Discord/i,              // Discord
    /Slack/i,                // Slack
  ];

  // Check explicit patterns first
  if (inAppPatterns.some(pattern => pattern.test(ua))) {
    return true;
  }

  // Generic WebView detection for iOS
  // iOS WebView: has Safari in UA but missing "Safari" at the end (standalone Safari always ends with Safari/xxx)
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  if (isIOS) {
    // Standalone Safari has "Safari/xxx" at the end
    // WebViews typically have Safari in UA but not at the end, or are missing it
    const hasSafariEngine = /AppleWebKit/i.test(ua);
    const isStandaloneSafari = /Safari\/\d/i.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua);
    const isChrome = /CriOS/i.test(ua);
    const isFirefox = /FxiOS/i.test(ua);

    // If it has WebKit but isn't Safari, Chrome, or Firefox, it's likely a WebView
    if (hasSafariEngine && !isStandaloneSafari && !isChrome && !isFirefox) {
      return true;
    }
  }

  // Generic WebView detection for Android
  const isAndroid = /Android/i.test(ua);
  if (isAndroid) {
    // Android WebView typically contains "wv" or "Version/x.x" without "Chrome"
    if (/\bwv\b/i.test(ua)) {
      return true;
    }
    // Old WebView detection: has Version/x.x but no Chrome
    if (/Version\/\d/.test(ua) && !/Chrome\/\d/.test(ua)) {
      return true;
    }
  }

  return false;
}

// Copy URL to clipboard
async function copyCurrentUrl(): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(window.location.href);
    return true;
  } catch {
    // Fallback for older browsers
    const input = document.createElement("input");
    input.value = window.location.href;
    document.body.appendChild(input);
    input.select();
    const success = document.execCommand("copy");
    document.body.removeChild(input);
    return success;
  }
}

export function GoogleSignInButton({ className }: GoogleSignInButtonProps) {
  const { signInWithGoogle, isGoogleLoaded, isLoading } = useAuthContext();
  const { t } = useI18n();
  const buttonRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef(false);
  const [isInApp, setIsInApp] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loadTimeout, setLoadTimeout] = useState(false);

  // Check if in-app browser on mount
  useEffect(() => {
    setIsInApp(isInAppBrowser());
  }, []);

  // Set a timeout for Google button loading
  useEffect(() => {
    // No timeout needed for in-app browsers or if already loaded
    if (isInApp || isGoogleLoaded) {
      setLoadTimeout(false);
      return;
    }

    const timer = setTimeout(() => {
      setLoadTimeout(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [isGoogleLoaded, isInApp]);

  useEffect(() => {
    if (buttonRef.current && isGoogleLoaded && !renderedRef.current && !isInApp) {
      renderedRef.current = true;
      signInWithGoogle(buttonRef.current);
    }
  }, [isGoogleLoaded, signInWithGoogle, isInApp]);

  const handleCopyUrl = async () => {
    const success = await copyCurrentUrl();
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isLoading) {
    return (
      <div className={`google-signin-loading ${className || ""}`}>
        <span className="btn-spinner" />
        <span>{t.auth?.loading || "Loading..."}</span>
      </div>
    );
  }

  // Show in-app browser warning
  if (isInApp) {
    return (
      <div className={`google-signin-inapp-warning ${className || ""}`}>
        <div className="inapp-warning-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <p className="inapp-warning-text">
          {t.auth?.inAppBrowserWarning || "Google Sign-In doesn't work in this browser. Please open in Safari or Chrome."}
        </p>
        <button className="inapp-copy-url-btn" onClick={handleCopyUrl}>
          {copied ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {t.auth?.urlCopied || "URL Copied!"}
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              {t.auth?.copyUrl || "Copy URL to open in browser"}
            </>
          )}
        </button>
      </div>
    );
  }

  // Show timeout fallback - same as in-app browser warning (likely undetected WebView)
  if (loadTimeout && !isGoogleLoaded) {
    return (
      <div className={`google-signin-inapp-warning ${className || ""}`}>
        <div className="inapp-warning-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <p className="inapp-warning-text">
          {t.auth?.inAppBrowserWarning || "Google Sign-In doesn't work in this browser. Please open in Safari or Chrome."}
        </p>
        <button className="inapp-copy-url-btn" onClick={handleCopyUrl}>
          {copied ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {t.auth?.urlCopied || "URL Copied!"}
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              {t.auth?.copyUrl || "Copy URL to open in browser"}
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className={`google-signin-wrapper ${className || ""}`}>
      <div ref={buttonRef} className="google-signin-button" />
      {!isGoogleLoaded && (
        <div className="google-signin-fallback">
          <span className="btn-spinner" />
        </div>
      )}
    </div>
  );
}
